import { InvoiceCommercialResponse } from "@/api";
import React from "react";

/*
 * ==================================================
 * INVOICE COMMERCIAL VIEW
 * ==================================================
 *
 * View-only representation of invoice commercials.
 *
 * Responsibilities:
 * - Display commercial summary
 * - Display calculated amounts
 * - Display payment mode
 * - Display validity
 *
 * NOT responsible for:
 * - Updating commercial discount
 * - Updating round off
 * - Changing payment mode
 * - Recalculating invoice totals
 * - Calling onChange
 *
 * Editing belongs in InvoiceCommercialEdit.
 */

export interface InvoiceCommercialViewProps {
  value: InvoiceCommercialResponse;
}

export default function InvoiceCommercialView({
  value,
}: InvoiceCommercialViewProps) {
  return (
    <section className="rounded-xl border bg-white p-6">
      {/* -----------------------------------------
          HEADER
      ----------------------------------------- */}
      <div className="grid gap-8 lg:grid-cols-2">
        {/* =========================================
            LEFT — COMMERCIAL SUMMARY
        ========================================= */}

        <div>

          <div className="space-y-3">
            <SummaryRow
              label="Currency"
              value={value.currency ?? "INR"}
              isCurrency={false}
            />

            <SummaryRow
              label="Subtotal"
              value={value.subtotal_amount}
            />

            <SummaryRow
              label="Items Discount"
              value={
                value.items_discount_amount
              }
            />

            <SummaryRow
              label="Commercial Discount"
              value={
                value.commercial_discount_amount
              }
            />

            <SummaryRow
              label="Total Discount"
              value={
                value.total_discount_amount
              }
            />

            <SummaryRow
              label="Tax"
              value={value.tax_amount}
            />

            <SummaryRow
              label="Round Off"
              value={value.round_off_amount}
            />

            {/* -------------------------------------
                FINAL AMOUNT
            ------------------------------------- */}

            <div className="border-t pt-3">
              <SummaryRow
                label="Final Amount"
                value={value.final_amount}
                emphasized
              />
            </div>

            {/* -------------------------------------
                VALID UNTIL
            ------------------------------------- */}

            <SummaryRow
              label="Valid Until"
              value={
                value.valid_until ?? "—"
              }
              isCurrency={false}
            />
          </div>
        </div>

        {/* =========================================
            RIGHT — COMMERCIAL DETAILS
        ========================================= */}

        <div>
          <h3 className="mb-4 text-sm font-semibold text-gray-700">
            Commercial Details
          </h3>

          <div className="space-y-3">
            <SummaryRow
              label="Commercial Discount"
              value={
                value.commercial_discount_amount
              }
            />

            <SummaryRow
              label="Round Off"
              value={
                value.round_off_amount
              }
            />

            <SummaryRow
              label="Payment Mode"
              value={
                formatPaymentMode(
                  value.payment_mode,
                )
              }
              isCurrency={false}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

/*
 * ==================================================
 * SUMMARY ROW
 * ==================================================
 */

interface SummaryRowProps {
  label: string;

  value?: number | string | null;

  /**
   * Defaults to true.
   *
   * Numeric commercial values are displayed
   * as INR currency.
   */
  isCurrency?: boolean;

  emphasized?: boolean;
}

function SummaryRow({
  label,
  value,
  isCurrency = true,
  emphasized = false,
}: SummaryRowProps) {
  const displayValue =
    value === null ||
    value === undefined ||
    String(value).trim() === ""
      ? "—"
      : isCurrency
        ? formatCurrency(
            Number(value),
          )
        : String(value);

  return (
    <div className="flex items-center justify-between gap-4">
      <span
        className={
          emphasized
            ? "text-sm font-semibold text-gray-900"
            : "text-sm text-gray-500"
        }
      >
        {label}
      </span>

      <span
        className={
          emphasized
            ? "text-base font-semibold text-gray-900"
            : "text-sm font-medium text-gray-900"
        }
      >
        {displayValue}
      </span>
    </div>
  );
}

/*
 * ==================================================
 * CURRENCY
 * ==================================================
 */

function formatCurrency(
  value: number,
): string {
  if (!Number.isFinite(value)) {
    return "₹0.00";
  }

  return `₹${value.toFixed(2)}`;
}

/*
 * ==================================================
 * PAYMENT MODE
 * ==================================================
 *
 * Keep the backend value intact elsewhere.
 * This is only a display transformation.
 */

function formatPaymentMode(
  value?: string | null,
): string {
  if (!value) {
    return "—";
  }

  switch (value) {
    case "BANK_TRANSFER":
      return "Bank Transfer";

    case "ONLINE":
      return "Online";

    default:
      return value;
  }
}